import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Unstable_Grid2/Grid2";
import TabPanel, { TabPanelProps } from "@mui/lab/TabPanel";
import {
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  ListSubheaderProps,
  Paper,
  Typography,
} from "@mui/material";

import { PatientData } from "../models/patient-data";
import { DateTime } from "./primitives/DateTime";
import React from "react";

const listSubheaderProps: ListSubheaderProps = {
  sx: {
    bgcolor: "background.default",
    borderBottom: "0.75px solid",
    borderColor: "divider",
    color: "text.primary",
    fontWeight: 500,
  },
};

export const CrisisTab: React.FC<TabPanelProps & { data: PatientData }> = ({
  data: { data },
  ...tabPanelProps
}) => {
  const { t } = useTranslation();

  const crisis = data?.crisisPlan?.[0];

  return (
    <TabPanel {...tabPanelProps}>
      <Grid container spacing={{ xs: 2, md: 4 }}>
        <Grid xs={12} order={crisis ? 0 : 3}>
          <Paper variant="outlined" sx={{ overflow: "hidden" }}>
            <List
              sx={{ maxHeight: 400, overflow: "auto", scrollbarWidth: "thin" }}
              dense
              disablePadding
              subheader={
                <ListSubheader {...listSubheaderProps}>
                  {t("tabs.more.crisis-label")}
                </ListSubheader>
              }
            >
              {crisis?.lastUpdateTimestamp ? (
                <>
                  <ListItem divider>
                    <ListItemText
                      primary={
                        <Grid container spacing={{ xs: 0, md: 2 }}>
                          <Grid xs={12} md={3}>
                            <Typography
                              color="text.secondary"
                              noWrap
                              variant="body2"
                            >
                              {t("tabs.more.crisis-plan")}
                            </Typography>
                          </Grid>
                          <Grid xs={12} md={9}>
                            <Typography variant="body2">
                              {crisis.crisisPlan
                                ?.split("\n")
                                .map((line, index, array) => (
                                  <React.Fragment key={`plan-${crisis.id}`}>
                                    {line}
                                    {index < array.length - 1 && <br />}
                                  </React.Fragment>
                                )) || t("common.no-data")}
                            </Typography>
                          </Grid>
                        </Grid>
                      }
                    />
                  </ListItem>

                  <ListItem divider>
                    <ListItemText
                      primary={
                        <Grid container spacing={{ xs: 0, md: 2 }}>
                          <Grid xs={12} md={3}>
                            <Typography
                              color="text.secondary"
                              noWrap
                              variant="body2"
                            >
                              {t("tabs.more.crisis-signs")}
                            </Typography>
                          </Grid>
                          <Grid xs={12} md={9}>
                            <Typography variant="body2">
                              {crisis.crisisAlerts
                                ?.split("\n")
                                .map((line, index, array) => (
                                  <React.Fragment key={`alert-${crisis.id}`}>
                                    {line}
                                    {index < array.length - 1 && <br />}
                                  </React.Fragment>
                                )) || t("common.no-data")}
                            </Typography>
                          </Grid>
                        </Grid>
                      }
                    />
                  </ListItem>

                  <ListItem divider>
                    <ListItemText
                      primary={
                        <Grid container spacing={{ xs: 0, md: 2 }}>
                          <Grid xs={12} md={3}>
                            <Typography
                              color="text.secondary"
                              noWrap
                              variant="body2"
                            >
                              {t("tabs.more.crisis-measures")}
                            </Typography>
                          </Grid>
                          <Grid xs={12} md={9}>
                            <Typography variant="body2">
                              {crisis.longTermMeasures
                                ?.split("\n")
                                .map((line, index, array) => (
                                  <React.Fragment key={`lt-${crisis.id}`}>
                                    {line}
                                    {index < array.length - 1 && <br />}
                                  </React.Fragment>
                                )) || t("common.no-data")}
                            </Typography>
                          </Grid>
                        </Grid>
                      }
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemText
                      primary={
                        <Grid container spacing={{ xs: 0.5, md: 2 }}>
                          <Grid xs={12} md={3}>
                            <Typography
                              color="text.secondary"
                              noWrap
                              variant="body2"
                            >
                              {t("tabs.more.crisis-update")}
                            </Typography>
                          </Grid>
                          <Grid xs={12} md={9}>
                            <Typography
                              alignItems="center"
                              color="text.secondary"
                              display="flex"
                              gap={1}
                              variant="body2"
                            >
                              <DateTime
                                timestamp={crisis.lastUpdateTimestamp}
                              />
                            </Typography>
                          </Grid>
                        </Grid>
                      }
                    />
                  </ListItem>
                </>
              ) : (
                <ListItem>
                  <ListItemText
                    primary={t("tabs.more.crisis-no-data")}
                    primaryTypographyProps={{
                      align: "center",
                      color: "text.secondary",
                    }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
          <>{}</>
        </Grid>
      </Grid>
    </TabPanel>
  );
};

export default CrisisTab;
